import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { LEGACY_PANEL_GEOMETRY_FALLBACK } from '../../types';
import type { PreviewPage, PreviewPanelGeometry } from '../../types';
import { usePanelMove } from '../../use-panel-move';
import { usePanelResize } from '../../use-panel-resize';

/**
 * PanelMoveOverlay — Position-1 Commit-3 drag surface (Sebastian
 * rulings 2026-09-03) plus resizable-panel handles (Sebastian ruling
 * 2026-09-17). Option 2 of the change dialog on the Freigabe screen.
 *
 * ═══ WHAT THE CUSTOMER SEES ═════════════════════════════════════════
 *
 * The raw scene image (no panel overlay) as the background, the
 * currently-DRAWN panel as a semi-transparent white rectangle with a
 * dashed border, the current scene text inside it, and a subtle grey
 * margin-cage line showing where the panel will stop. On BOTH side
 * edges there is a resize handle; on the panel body itself, a move
 * grab area. Mouse, touch and stylus all work; the panel bumps into
 * the margin cage rather than passing through it.
 *
 * `Speichern (Position)` becomes active once the panel has moved.
 * `Speichern (Breite)` becomes active once the panel has resized.
 * `Abbrechen` discards.
 *
 * ═══ DRAWN GEOMETRY, NOT DETECTED (Sebastian ruling 2026-09-03 eve) ═
 *
 * The initial outline geometry is `props.initialDrawnPanel` — the
 * composer's own kernel run server-side against the current scene
 * text + current (detected-or-customer) left/top/width. Doc fractions
 * [0,1]. This is what `drawSceneText` actually draws.
 *
 * After a resize round-trip the server returns the new drawn geometry
 * (auto-widen may have widened the panel beyond what the customer
 * requested); the client renders exactly what the server returns.
 *
 * ═══ TEXT INSIDE THE OUTLINE (Sebastian ruling 2026-09-03 evening) ═
 *
 * Merriweather at the composer's 13pt-equivalent, wrapped to the
 * writable width the composer would wrap to, clipped on overflow.
 * The client-side wrap approximation can only differ from the
 * composer by a line-break decision (browser vs pdf-lib break
 * heuristics on the exact same font metrics) — the composer stays
 * authoritative on save.
 *
 * ═══ MARGIN CAGE — SINGLE SOURCE OF TRUTH ═══════════════════════════
 *
 * Frame margins (top / bottom / side), min + max width, max height
 * fraction and the 300-char cap all come from the envelope's
 * `panelGeometry` bundle (`PreviewPanelGeometry`). The PWA does not
 * hardcode any of these numbers; a backend change to any constant
 * takes effect on the next envelope read.
 *
 * ═══ RESIZE OUTLINE-ONLY DURING DRAG ════════════════════════════════
 *
 * While a side handle is being dragged the panel body renders as a
 * DASHED OUTLINE with no fill and no text — the server has not yet
 * seen the new width, and any client-side wrap approximation would
 * lie. On pointer release the client fires the resize endpoint and
 * shows a loading state; the server returns the drawn panel with the
 * effective width (post-auto-widen) and the outline snaps to it with
 * text + fill restored.
 */

export interface PanelMoveOverlayProps {
  readonly bookOrderId: string;
  readonly sceneId: string;
  readonly initialDrawnPanel: NonNullable<PreviewPage['drawnPanel']>;
  readonly sceneText: string;
  readonly rawImageUrl: string;
  /**
   * OPTIONAL (Sebastian ruling 2026-09-17 fix-4 deploy-order
   * safety). Cloudflare Pages can serve the new PWA before Railway
   * rolls out the new backend; the old backend's envelope has no
   * `panelGeometry` field. When undefined the overlay falls back to
   * `LEGACY_PANEL_GEOMETRY_FALLBACK` for the move-cage frame values
   * AND hides the resize handles (resize needs a backend endpoint
   * the old backend also lacks). Move continues to work via the
   * pre-slice endpoint.
   */
  readonly panelGeometry?: PreviewPanelGeometry;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

/**
 * Handle strip width — a UI dimension, not a geometry number.
 * Sits on top of the panel's side edges and captures pointer events.
 * Wider than a hairline so a touch reliably lands, narrow enough to
 * keep the panel-body drag area usable.
 */
const HANDLE_PX = 14;

type DragMode = 'idle' | 'move' | 'resize-left' | 'resize-right';

export function PanelMoveOverlay({
  bookOrderId,
  sceneId,
  initialDrawnPanel,
  sceneText,
  rawImageUrl,
  panelGeometry,
  onClose,
  onSaved,
}: PanelMoveOverlayProps) {
  // Sebastian ruling 2026-09-17 fix-4 — deploy-order safety. Fall
  // back to the pre-slice hard-coded frame values when the envelope
  // omits `panelGeometry` (old backend). Resize handles are hidden
  // via `resizeSurfaceAvailable` below so the customer cannot fire
  // a resize endpoint the old backend does not implement.
  const geometry = panelGeometry ?? LEGACY_PANEL_GEOMETRY_FALLBACK;
  const resizeSurfaceAvailable = panelGeometry !== undefined;
  const {
    topMarginFrac,
    bottomMarginFrac,
    sideMarginFrac,
    minWidthFrac,
    maxWidthFrac,
    sceneTextFontFrac,
    sceneTextLeadingMult,
  } = geometry;

  // Current panel geometry — starts from the server-supplied drawn
  // panel and updates as the customer moves/resizes.
  const [left, setLeft] = useState(initialDrawnPanel.left);
  const [top, setTop] = useState(initialDrawnPanel.top);
  const [width, setWidth] = useState(initialDrawnPanel.width);
  const [height, setHeight] = useState(initialDrawnPanel.height);
  const [writableWidth, setWritableWidth] = useState(initialDrawnPanel.writableWidth);
  // Baseline geometry — what the server currently persists. Compared
  // against the current state to decide which Save button is active.
  const [savedLeft, setSavedLeft] = useState(initialDrawnPanel.left);
  const [savedTop, setSavedTop] = useState(initialDrawnPanel.top);
  const [savedWidth, setSavedWidth] = useState(initialDrawnPanel.width);
  // Drag-time width preview (fraction) — while a resize handle is
  // being dragged this holds the intermediate width. On release the
  // client fires the endpoint and the server-returned width lands in
  // `width`.
  const [dragWidthFrac, setDragWidthFrac] = useState<number | null>(null);
  const [dragMode, setDragMode] = useState<DragMode>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  const move = usePanelMove(bookOrderId);
  const resize = usePanelResize(bookOrderId);

  // Clamp bounds — recomputed against the current width so the
  // move-cage tightens as the panel widens.
  const minLeft = sideMarginFrac;
  const maxLeft = 1 - sideMarginFrac - width;
  const minTop = topMarginFrac;
  const maxTop = 1 - bottomMarginFrac - height;

  const imageRef = useRef<HTMLDivElement | null>(null);
  const [containerPx, setContainerPx] = useState(0);
  useLayoutEffect(() => {
    const el = imageRef.current;
    if (!el) return;
    const measure = () => setContainerPx(el.getBoundingClientRect().width);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const dragAnchorLeftRef = useRef<number>(0);
  const dragAnchorRightRef = useRef<number>(0);

  // ───── MOVE (panel body pointer down) ────────────────────────────
  const handleBodyPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!imageRef.current) return;
      if (resize.saving || move.saving) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      const rect = imageRef.current.getBoundingClientRect();
      const panelLeftPx = left * rect.width;
      const panelTopPx = top * rect.height;
      dragOffsetRef.current = {
        x: e.clientX - rect.left - panelLeftPx,
        y: e.clientY - rect.top - panelTopPx,
      };
      setDragMode('move');
    },
    [left, top, resize.saving, move.saving],
  );

  // ───── RESIZE LEFT / RIGHT (handle pointer down) ─────────────────
  const beginResize = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, side: 'left' | 'right') => {
      if (!imageRef.current) return;
      if (resize.saving || move.saving) return;
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      // Anchor the OPPOSITE edge so a widen/narrow keeps that edge
      // fixed. Customer sees the panel grow AWAY from the anchor.
      dragAnchorLeftRef.current = left;
      dragAnchorRightRef.current = left + width;
      setDragMode(side === 'left' ? 'resize-left' : 'resize-right');
      setDragWidthFrac(width);
    },
    [left, width, resize.saving, move.saving],
  );

  const handleContainerPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!imageRef.current || dragMode === 'idle') return;
      const rect = imageRef.current.getBoundingClientRect();

      if (dragMode === 'move') {
        if (dragOffsetRef.current === null) return;
        const nextLeftPx = e.clientX - rect.left - dragOffsetRef.current.x;
        const nextTopPx = e.clientY - rect.top - dragOffsetRef.current.y;
        const nextLeftFrac = nextLeftPx / rect.width;
        const nextTopFrac = nextTopPx / rect.height;
        setLeft(Math.min(Math.max(nextLeftFrac, minLeft), maxLeft));
        setTop(Math.min(Math.max(nextTopFrac, minTop), maxTop));
        return;
      }

      // Resize — pointer x defines the moving edge.
      const pointerFrac = (e.clientX - rect.left) / rect.width;
      if (dragMode === 'resize-right') {
        const requestedRight = Math.max(pointerFrac, dragAnchorLeftRef.current + minWidthFrac);
        const clampedRight = Math.min(requestedRight, 1 - sideMarginFrac);
        const nextWidth = Math.min(
          Math.max(clampedRight - dragAnchorLeftRef.current, minWidthFrac),
          maxWidthFrac,
        );
        setDragWidthFrac(nextWidth);
      } else if (dragMode === 'resize-left') {
        const requestedLeft = Math.min(pointerFrac, dragAnchorRightRef.current - minWidthFrac);
        const clampedLeft = Math.max(requestedLeft, sideMarginFrac);
        const nextWidth = Math.min(
          Math.max(dragAnchorRightRef.current - clampedLeft, minWidthFrac),
          maxWidthFrac,
        );
        setDragWidthFrac(nextWidth);
        setLeft(Math.max(dragAnchorRightRef.current - nextWidth, sideMarginFrac));
      }
    },
    [dragMode, minLeft, maxLeft, minTop, maxTop, minWidthFrac, maxWidthFrac, sideMarginFrac],
  );

  const handleContainerPointerUp = useCallback(
    async (e: React.PointerEvent<HTMLDivElement>) => {
      const finishedMode = dragMode;
      if (finishedMode === 'idle') return;
      if ((e.currentTarget as Element).hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      dragOffsetRef.current = null;
      setDragMode('idle');

      if (finishedMode === 'resize-left' || finishedMode === 'resize-right') {
        const requestedWidth = dragWidthFrac;
        setDragWidthFrac(null);
        if (requestedWidth === null) return;
        // Fire the server; render loading state via `resize.saving`.
        setSaveError(null);
        const result = await resize.save(sceneId, requestedWidth);
        if (result.ok) {
          setLeft(result.response.panel.left);
          setTop(result.response.panel.top);
          setWidth(result.response.panel.width);
          setHeight(result.response.panel.height);
          setWritableWidth(result.response.panel.writableWidth);
          setSavedLeft(result.response.panel.left);
          setSavedWidth(result.response.panel.width);
        } else {
          // Snap back to the pre-drag saved geometry and surface the
          // envelope's German user_message (e.g. `scene_text_panel_overflow`
          // → "Der Text passt nicht in die Tafel. Bitte kürze ihn.").
          setLeft(savedLeft);
          setWidth(savedWidth);
          const envelopeMessage = result.error?.envelope?.user_message ?? null;
          setSaveError(envelopeMessage ?? STRINGS.book.review.editorSaveError);
        }
      }
    },
    [dragMode, dragWidthFrac, resize, sceneId, savedLeft, savedWidth],
  );

  // ───── SAVE (position) — the existing panel-move button ──────────
  const positionChanged = left !== savedLeft || top !== savedTop;
  const handleSavePosition = useCallback(async () => {
    setSaveError(null);
    const result = await move.save(sceneId, left, top);
    if (result.ok) {
      setSavedLeft(result.response.panelBox.left);
      setSavedTop(result.response.panelBox.top);
      onSaved();
      onClose();
    } else {
      const envelopeMessage = result.error?.envelope?.user_message ?? null;
      setSaveError(envelopeMessage ?? STRINGS.book.review.editorSaveError);
    }
  }, [move, sceneId, left, top, onSaved, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Container is aspect-1:1 so container width equals container height.
  // Envelope's `sceneTextFontFrac` = composer's SCENE_TEXT_PT / doc
  // height in pt (~0.0212). Multiplying by the container's rendered
  // width in CSS px gives the font-size in CSS px.
  const fontSizePx = containerPx > 0 ? sceneTextFontFrac * containerPx : 0;
  const paddingXFrac = (width - writableWidth) / 2 / width;

  // Effective width to render — during a resize drag we show the
  // drag-preview width in an outline-only style; otherwise the
  // saved-server-truth width with fill + text.
  const outlineOnly = dragMode === 'resize-left' || dragMode === 'resize-right';
  const renderedWidthFrac = outlineOnly && dragWidthFrac !== null ? dragWidthFrac : width;

  return (
    <div className="space-y-3">
      <h2 id="page-change-dialog-heading" className="text-lg font-semibold text-dream-navy pr-6">
        {STRINGS.book.review.movePanelHeading}
      </h2>

      <div
        ref={imageRef}
        className="relative w-full overflow-hidden rounded-lg bg-dream-navy/5"
        style={{ aspectRatio: '1 / 1' }}
        onPointerMove={handleContainerPointerMove}
        onPointerUp={handleContainerPointerUp}
        onPointerCancel={handleContainerPointerUp}
      >
        <img
          src={rawImageUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-contain"
          draggable={false}
        />

        {/* Margin cage — subtle grey outline at the composer's clamps. */}
        <div
          aria-hidden="true"
          className="absolute border border-dream-navy/20 pointer-events-none"
          style={{
            left: `${sideMarginFrac * 100}%`,
            top: `${topMarginFrac * 100}%`,
            right: `${sideMarginFrac * 100}%`,
            bottom: `${bottomMarginFrac * 100}%`,
          }}
        />

        {/* Panel body */}
        <div
          role="button"
          tabIndex={0}
          aria-label={STRINGS.book.review.movePanelAriaLabel}
          onPointerDown={handleBodyPointerDown}
          className={
            outlineOnly
              ? 'absolute border-2 border-dashed border-dream-navy bg-transparent cursor-grabbing overflow-hidden'
              : 'absolute border-2 border-dashed border-dream-navy bg-white/90 cursor-grab active:cursor-grabbing overflow-hidden'
          }
          style={{
            left: `${left * 100}%`,
            top: `${top * 100}%`,
            width: `${renderedWidthFrac * 100}%`,
            height: `${height * 100}%`,
            touchAction: 'none',
          }}
        >
          {!outlineOnly && (
            <div
              aria-hidden="true"
              className="absolute text-dream-navy pointer-events-none"
              style={{
                left: `${paddingXFrac * 100}%`,
                right: `${paddingXFrac * 100}%`,
                top: `${paddingXFrac * 100}%`,
                bottom: `${paddingXFrac * 100}%`,
                fontFamily: "'Merriweather', Georgia, serif",
                fontWeight: 400,
                fontSize: `${fontSizePx}px`,
                lineHeight: sceneTextLeadingMult,
                overflow: 'hidden',
                wordBreak: 'normal',
                overflowWrap: 'break-word',
              }}
            >
              {sceneText}
            </div>
          )}

          {/* Resize handles — hidden when the envelope omits
              `panelGeometry` (old backend, deploy-order safety).
              Resize needs the new backend endpoint, so we hide the
              grips rather than let the customer fire a 404. */}
          {resizeSurfaceAvailable && (
            <>
              <div
                role="separator"
                aria-label={STRINGS.book.review.resizeHandleLeftAriaLabel}
                aria-orientation="vertical"
                onPointerDown={(e) => beginResize(e, 'left')}
                className="absolute top-0 bottom-0 left-0 cursor-ew-resize bg-dream-navy/30 hover:bg-dream-navy/60"
                style={{ width: `${HANDLE_PX}px`, touchAction: 'none' }}
              />
              <div
                role="separator"
                aria-label={STRINGS.book.review.resizeHandleRightAriaLabel}
                aria-orientation="vertical"
                onPointerDown={(e) => beginResize(e, 'right')}
                className="absolute top-0 bottom-0 right-0 cursor-ew-resize bg-dream-navy/30 hover:bg-dream-navy/60"
                style={{ width: `${HANDLE_PX}px`, touchAction: 'none' }}
              />
            </>
          )}
        </div>

        {resize.saving && (
          <div
            aria-live="polite"
            className="absolute inset-x-0 bottom-2 flex justify-center pointer-events-none"
          >
            <span className="rounded-full bg-dream-navy/80 px-3 py-1 text-xs font-medium text-dream-white">
              {STRINGS.book.review.resizeSaving}
            </span>
          </div>
        )}
      </div>

      {saveError !== null && (
        <p role="alert" className="text-sm text-error">
          {saveError}
        </p>
      )}

      <div className="flex gap-3 justify-end pt-1">
        <button
          type="button"
          onClick={onClose}
          disabled={move.saving || resize.saving}
          className="h-10 rounded-lg border border-dream-navy/30 px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy/5 disabled:opacity-40"
        >
          {STRINGS.book.review.movePanelCancel}
        </button>
        <button
          type="button"
          onClick={handleSavePosition}
          disabled={move.saving || resize.saving || !positionChanged}
          className="h-10 rounded-lg bg-dream-navy px-4 text-sm font-semibold text-dream-white hover:bg-dream-navy/90 disabled:opacity-40"
        >
          {STRINGS.book.review.movePanelSave}
        </button>
      </div>
    </div>
  );
}
